import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Trash2, Loader2, X } from "lucide-react";
import { usePostAuth } from "../../../hooks/usePostAuth";

interface DeleteButtonProps {
  id: number;
  email: string;
  onDeleteSuccess?: () => void;
}

const DeleteButton: React.FC<DeleteButtonProps> = ({ id, onDeleteSuccess }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const { mutate, isPending } = usePostAuth(`users/${id}`, {
    queryKeyToInvalidate: ["get-alumnis"],
  });

  const handleDelete = () => {
    mutate(
      { _method: "DELETE" },
      {
        onSuccess: () => {
          setShowConfirm(false);
          onDeleteSuccess?.();
          window.location.reload();
        },
        onError: (error) => {
          console.error("Delete error:", error);
          // Alert removed as requested
          setShowConfirm(false);
          window.location.reload(); // Force reload even on error
        },
      }
    );
  };

  const openConfirm = () => {
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    setShowConfirm(false);
  };

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={openConfirm}
        disabled={isPending}
        className={`
          inline-flex items-center gap-1 px-3 py-2 
          bg-red-500 hover:bg-red-600 disabled:bg-red-400 
          text-white rounded-md text-sm font-medium 
          transition-colors duration-200
        `}
        title="Delete Alumni"
        aria-label="Delete alumni"
      >
        {isPending ? (
          <Loader2 className="animate-spin" size={16} />
        ) : (
          <Trash2 size={16} />
        )}
        <span className="hidden sm:inline">
          {isPending ? "Deleting…" : "Delete"}
        </span>
      </button>

      {showConfirm &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeConfirm();
            }}
          >
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete Alumni?
                </h3>
                <button
                  onClick={closeConfirm}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                This action <strong>cannot be undone</strong>. The alumni record
                will be permanently removed.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={closeConfirm}
                  disabled={isPending}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-300 text-gray-700 rounded-md font-medium transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isPending}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-red-400 text-white rounded-md font-medium transition flex items-center gap-2"
                >
                  {isPending && <Loader2 className="animate-spin" size={16} />}
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default DeleteButton;
